import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin')) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Public API
export const publicApi = {
  // Hero Slides
  getHeroSlides: () => api.get('/hero-slides'),
  
  // Services
  getServices: () => api.get('/services'),
  getServiceBySlug: (slug) => api.get(`/services/${slug}`),
  
  // Pricing
  getPricing: () => api.get('/pricing'),
  
  // Testimonials
  getTestimonials: () => api.get('/testimonials'),
  
  // Blog
  getBlogPosts: (params) => api.get('/blog', { params }),
  getBlogCount: (params) => api.get('/blog/count', { params }),
  getBlogPost: (slug) => api.get(`/blog/${slug}`),
  getRecentPosts: (count = 3) => api.get(`/blog/recent/${count}`),
  getBlogArchives: () => api.get('/blog/archives/list'),
  
  // Categories
  getCategories: () => api.get('/categories'),
  
  // Site Settings
  getSiteSettings: () => api.get('/site-settings'),
  
  // Free Resources
  getFreeResources: () => api.get('/free-resources'),
  
  // Pages
  getPage: (slug) => api.get(`/pages/${slug}`),
  
  // Form Submission
  submitForm: (data) => api.post('/submit-form', data),
};

// Admin API
export const adminApi = {
  // Auth
  login: (credentials) => api.post('/admin/login', credentials),
  register: (data) => api.post('/admin/register', data),
  verify: () => api.get('/admin/verify'),
  
  // Dashboard
  getStats: () => api.get('/admin/stats'),
  
  // Hero Slides
  getHeroSlides: () => api.get('/admin/hero-slides'),
  createHeroSlide: (data) => api.post('/admin/hero-slides', data),
  updateHeroSlide: (id, data) => api.put(`/admin/hero-slides/${id}`, data),
  deleteHeroSlide: (id) => api.delete(`/admin/hero-slides/${id}`),
  
  // Services
  getServices: () => api.get('/admin/services'),
  createService: (data) => api.post('/admin/services', data),
  updateService: (id, data) => api.put(`/admin/services/${id}`, data),
  deleteService: (id) => api.delete(`/admin/services/${id}`),
  
  // Coaching Services (Individual 1-on-1 services)
  getCoachingServices: () => api.get('/admin/coaching-services'),
  createCoachingService: (data) => api.post('/admin/coaching-services', data),
  updateCoachingService: (id, data) => api.put(`/admin/coaching-services/${id}`, data),
  deleteCoachingService: (id) => api.delete(`/admin/coaching-services/${id}`),
  
  // Pricing
  getPricing: () => api.get('/admin/pricing'),
  createPricing: (data) => api.post('/admin/pricing', data),
  updatePricing: (id, data) => api.put(`/admin/pricing/${id}`, data),
  deletePricing: (id) => api.delete(`/admin/pricing/${id}`),
  
  // Testimonials
  getTestimonials: () => api.get('/admin/testimonials'),
  createTestimonial: (data) => api.post('/admin/testimonials', data),
  updateTestimonial: (id, data) => api.put(`/admin/testimonials/${id}`, data),
  deleteTestimonial: (id) => api.delete(`/admin/testimonials/${id}`),
  
  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  
  // Blog Posts
  getBlogPosts: () => api.get('/admin/blog'),
  getBlogPost: (id) => api.get(`/admin/blog/${id}`),
  createBlogPost: (data) => api.post('/admin/blog', data),
  updateBlogPost: (id, data) => api.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/${id}`),
  
  // Form Submissions
  getSubmissions: (params) => api.get('/admin/submissions', { params }),
  updateSubmissionStatus: (id, status) => api.put(`/admin/submissions/${id}/status?status=${status}`),
  deleteSubmission: (id) => api.delete(`/admin/submissions/${id}`),
  
  // Site Settings
  getSiteSettings: () => api.get('/admin/site-settings'),
  updateSiteSettings: (data) => api.put('/admin/site-settings', data),
  
  // Free Resources
  getFreeResources: () => api.get('/admin/free-resources'),
  createFreeResource: (data) => api.post('/admin/free-resources', data),
  updateFreeResource: (id, data) => api.put(`/admin/free-resources/${id}`, data),
  deleteFreeResource: (id) => api.delete(`/admin/free-resources/${id}`),
  
  // Media
  getMedia: () => api.get('/admin/media'),
  createMedia: (data) => api.post('/admin/media', data),
  deleteMedia: (id) => api.delete(`/admin/media/${id}`),
  
  // Pages
  getPages: () => api.get('/admin/pages'),
  getPage: (id) => api.get(`/admin/pages/${id}`),
  createPage: (data) => api.post('/admin/pages', data),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  deletePage: (id) => api.delete(`/admin/pages/${id}`),
  
  // Seed Data
  seedData: () => api.post('/admin/seed-data'),
  seedPages: () => api.post('/admin/seed-pages'),
  seedCoachingServices: () => api.post('/admin/seed-coaching-services'),
  seedPageContent: () => api.post('/admin/seed-page-content'),
  
  // Analytics
  getAnalytics: (period) => api.get('/admin/analytics', { params: { period } }),
  
  // FAQs
  getFaqs: () => api.get('/admin/faqs'),
  createFaq: (data) => api.post('/admin/faqs', data),
  updateFaq: (id, data) => api.put(`/admin/faqs/${id}`, data),
  deleteFaq: (id) => api.delete(`/admin/faqs/${id}`),
  
  // Stats Blocks
  getStatsBlocks: () => api.get('/admin/stats-blocks'),
  createStatsBlock: (data) => api.post('/admin/stats-blocks', data),
  updateStatsBlock: (id, data) => api.put(`/admin/stats-blocks/${id}`, data),
  deleteStatsBlock: (id) => api.delete(`/admin/stats-blocks/${id}`),
  
  // Team Members
  getTeamMembers: () => api.get('/admin/team-members'),
  createTeamMember: (data) => api.post('/admin/team-members', data),
  updateTeamMember: (id, data) => api.put(`/admin/team-members/${id}`, data),
  deleteTeamMember: (id) => api.delete(`/admin/team-members/${id}`),
  
  // Page Content
  getPageContent: (pageSlug) => api.get(`/admin/page-content/${pageSlug}`),
  createPageContent: (data) => api.post('/admin/page-content', data),
  updatePageContent: (id, data) => api.put(`/admin/page-content/${id}`, data),
  deletePageContent: (id) => api.delete(`/admin/page-content/${id}`),
  
  // Content Blocks (Page Builder)
  getContentBlocks: (pageId) => api.get(`/admin/content-blocks/${pageId}`),
  createContentBlock: (pageId, data) => api.post(`/admin/content-blocks?page_id=${pageId}`, data),
  updateContentBlock: (blockId, data) => api.put(`/admin/content-blocks/${blockId}`, data),
  deleteContentBlock: (blockId) => api.delete(`/admin/content-blocks/${blockId}`),
  reorderContentBlocks: (pageId, blockOrders) => api.put(`/admin/content-blocks/reorder/${pageId}`, blockOrders),
  
  // Page Versions (History/Rollback)
  getPageVersions: (pageId) => api.get(`/admin/page-versions/${pageId}`),
  createPageVersion: (pageId, changeSummary) => api.post(`/admin/page-versions?page_id=${pageId}&change_summary=${encodeURIComponent(changeSummary)}`),
  rollbackToVersion: (versionId) => api.post(`/admin/page-versions/${versionId}/rollback`),
  
  // Page Drafts (Draft/Publish Workflow)
  getPageDraft: (pageId) => api.get(`/admin/page-drafts/${pageId}`),
  savePageDraft: (pageId, draftData) => api.put(`/admin/page-drafts/${pageId}`, draftData),
  publishPageDraft: (pageId) => api.post(`/admin/page-drafts/${pageId}/publish`),
  discardPageDraft: (pageId) => api.delete(`/admin/page-drafts/${pageId}`),
  
  // Scheduled Publishing
  getScheduledPublishes: () => api.get('/admin/scheduled-publishes'),
  createScheduledPublish: (pageId, scheduledTime) => api.post(`/admin/scheduled-publishes?page_id=${pageId}&scheduled_time=${encodeURIComponent(scheduledTime)}`),
  cancelScheduledPublish: (scheduleId) => api.delete(`/admin/scheduled-publishes/${scheduleId}`),
  
  // Component Templates (Page Builder Library)
  getComponentTemplates: () => api.get('/admin/component-templates'),
  createComponentTemplate: (data) => api.post('/admin/component-templates', data),
  updateComponentTemplate: (id, data) => api.put(`/admin/component-templates/${id}`, data),
  deleteComponentTemplate: (id) => api.delete(`/admin/component-templates/${id}`),
  seedComponentTemplates: () => api.post('/admin/seed-component-templates'),
  
  // Page Sections (for advanced drag-and-drop)
  getPageSections: (pageId) => api.get(`/admin/page-sections/${pageId}`),
  createPageSection: (data) => api.post('/admin/page-sections', data),
  updatePageSection: (id, data) => api.put(`/admin/page-sections/${id}`, data),
  deletePageSection: (id) => api.delete(`/admin/page-sections/${id}`),
  reorderPageSections: (pageId, sectionIds) => {
    // Transform to expected format: [{id, order}, ...]
    const sectionOrders = sectionIds.map((id, index) => ({ id, order: index }));
    return api.put(`/admin/page-sections/reorder/${pageId}`, sectionOrders);
  },
  
  // A/B Testing
  getABTests: () => api.get('/admin/ab-tests'),
  getABTest: (id) => api.get(`/admin/ab-tests/${id}`),
  createABTest: (data) => api.post('/admin/ab-tests', data),
  updateABTest: (id, data) => api.put(`/admin/ab-tests/${id}`, data),
  deleteABTest: (id) => api.delete(`/admin/ab-tests/${id}`),
  startABTest: (id) => api.post(`/admin/ab-tests/${id}/start`),
  stopABTest: (id) => api.post(`/admin/ab-tests/${id}/stop`),
  getABTestResults: (id) => api.get(`/admin/ab-tests/${id}/results`),
  
  // Custom CSS/JS Per Page
  getPageCustomCode: (pageId) => api.get(`/admin/page-custom-code/${pageId}`),
  updatePageCustomCode: (pageId, data) => api.put(`/admin/page-custom-code/${pageId}`, data),
  
  // Form Submissions (for notifications)
  getFormSubmissions: () => api.get('/admin/submissions'),
};

export default api;
